import type { ReactElement, ReactNode } from 'react'

import styles from './table.module.scss'

export type TableColumn<TRow> = {
  /** Identity of the column inside this table — the React key of its cells. */
  key: string
  header: ReactNode
  render: (row: TRow) => ReactNode
  align?: 'left' | 'right'
  /** Hidden below the tablet breakpoint. For a column a phone can live without. */
  hideOnMobile?: boolean
}

export type TableProps<TRow> = {
  columns: Array<TableColumn<TRow>>
  rows: Array<TRow>
  /** The row's own identity — never its index, which changes when a row moves. */
  rowKey: (row: TRow) => string
  /** What the table is, for a screen reader. Rendered visually hidden. */
  caption: string
}

/**
 * A plain data table with big rows. It renders exactly the rows it is given: an
 * EMPTY list is not its business — a screen shows `EmptyState` instead, because
 * "no hay grupos" and "no pudimos cargar los grupos" must never look alike, and
 * an empty `<tbody>` looks like both.
 */
export const Table = <TRow,>({ columns, rows, rowKey, caption }: TableProps<TRow>): ReactElement => (
  <div className={styles.table__scroll}>
    <table className={styles.table}>
      <caption className="visually-hidden">{caption}</caption>
      <thead>
        <tr>
          {columns.map((column) => (
            <th
              key={column.key}
              scope="col"
              className={[
                column.align === 'right' ? styles['table__cell--right'] : '',
                column.hideOnMobile === true ? styles['table__cell--desktop'] : '',
              ].filter(Boolean).join(' ')}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={rowKey(row)}>
            {columns.map((column) => (
              <td
                key={column.key}
                className={[
                  column.align === 'right' ? styles['table__cell--right'] : '',
                  column.hideOnMobile === true ? styles['table__cell--desktop'] : '',
                ].filter(Boolean).join(' ')}
              >
                {column.render(row)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)
